import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import { resolveMediaUrl, responsiveSrcSet } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Renders the latest 3 published blog posts on the homepage.
 * Hidden gracefully if there are zero posts.
 */
const HomeBlogSection = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/blogs?limit=3`)
      .then((r) => {
        if (mounted && Array.isArray(r.data)) setPosts(r.data.slice(0, 3));
      })
      .catch((err) => console.warn("[HomeBlogSection] fetch failed:", err));
    return () => {
      mounted = false;
    };
  }, []);

  if (posts.length === 0) return null;

  return (
    <section
      data-testid="home-blog-section"
      className="border-b border-black/5 bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Latest from the blog
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Industry insights.
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="home-blog-view-all"
            className="group inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#1c1c1e] transition-all hover:border-[#e85a1c] hover:text-[#e85a1c]"
          >
            View all articles
            <ArrowRight
              size={13}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-7 md:grid-cols-3">
          {posts.map((b, i) => (
            <Reveal key={b.id} delay={i * 90}>
              <Link
                to={`/blog/${b.slug}`}
                data-testid={`home-blog-card-${b.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-black/5 bg-white shadow-lux transition-all duration-300 hover:-translate-y-1 hover:shadow-lux-hover"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#efefe9]">
                  {b.cover_image_url ? (
                    <img
                      src={resolveMediaUrl(b.cover_image_url)}
                      srcSet={responsiveSrcSet(b.cover_image_url, [320, 480, 640, 800])}
                      sizes="(min-width: 1024px) 32vw, (min-width: 640px) 48vw, 92vw"
                      alt={b.title}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center font-display text-6xl text-[#e85a1c]/30">
                      ·
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="font-mono-industrial text-[9px] uppercase tracking-[0.3em] text-[#e85a1c]">
                    {b.category}
                  </div>
                  <h3 className="mt-3 line-clamp-2 font-display-med text-xl uppercase leading-tight tracking-tight text-[#1c1c1e]">
                    {b.title}
                  </h3>
                  <p className="mt-3 line-clamp-3 flex-1 text-[13px] leading-[1.65] text-[#4a4a4e]">
                    {b.excerpt}
                  </p>
                  <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
                    <span className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                      {formatDate(b.published_at)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                      Read
                      <ArrowRight
                        size={11}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeBlogSection;
